/* eslint-disable no-nested-ternary */
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme, Spinner } from 'fpay-ui';
import { getEnv } from '@config';
import {
  advancementStatus as advancementStatusConstants,
  getAdvancementErrorContent,
} from '@constants/advancement';
import { useBreakpoints } from '@hooks/NewBreakpoint';
import useAdvancements from '@hooks/useAdvancements';
import { useSelector } from 'react-redux';

const { publicPath } = getEnv();

const fontTypes = {
  title: css`
    ${({ theme }) => theme.textTypes['Title-s22-w700']};
    color: ${({ theme }) => theme.colors.textBlack};
    line-height: 26px;
    text-align: center;
    ${({ theme }) => theme.newBreakpoint('sm')} {
      ${({ theme }) => theme.textTypes['Title-s18-w700']};
      line-height: 21px;
      letter-spacing: 0em;
      text-align: center;
    }
  `,
  subtitle: css`
    ${({ theme }) => theme.textTypes['Subtitle-1-w400']};
    color: ${({ theme }) => theme.colors.textBlack};
    line-height: 19px;
    text-align: center;
    ${({ theme }) => theme.newBreakpoint('sm')} {
      ${({ theme }) => theme.textTypes['Subtitle-1-w400']};
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0;
      text-align: center;
    }
  `,
};

const Layout = styled.div`
  height: 424px;
  padding: 0 32px 0 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${({ theme }) => theme.newBreakpoint('sm')} {
    height: 310px;
    padding: 0 24px 0 24px;
  }
`;

const StyledSpinner = styled.div`
  height: 96px;
  width: 96px;
  margin: 88px 0 56px 0;
  img {
    height: 96px;
    width: 96px;
  }
  ${({ theme }) => theme.newBreakpoint('sm')} {
    width: 55.41px;
    height: 55.41px;
    margin: 48px 0 48px 0;
    img {
      width: 55.41px;
      height: 55.41px;
    }
  }
`;

const Title = styled.div`
  ${fontTypes.title}
  margin-bottom: 8px;
`;

const Subtitle = styled.div`
  ${fontTypes.subtitle}
  margin-bottom: 112px;
  ${({ theme }) => theme.newBreakpoint('sm')} {
    margin-bottom: 64px;
  }
`;

const AdvancementStatus = ({ status }) => {
  const { t } = useTranslation();
  const { breakpoint } = useBreakpoints();
  const { checkStatusAdvancement, refreshDataAdvancement } = useAdvancements();
  const advancementStatus = useSelector((state) => state.advancements.advancementStatus);
  const { requestCounter: advancementRequestCounter, errorCode } = advancementStatus;

  const getContent = (type) => {
    let title;
    let subTitle;
    let iconPath;
    let alt;
    switch (type) {
      case advancementStatusConstants.PENDING:
        title = t('homeScreen.advancementStatus.pending.title');
        subTitle = t('homeScreen.advancementStatus.pending.subTitle');
        break;
      case advancementStatusConstants.PROCESSED:
        title = t('homeScreen.advancementStatus.processed.title');
        subTitle = t('homeScreen.advancementStatus.processed.subTitle');
        iconPath = `${publicPath}/check.svg`;
        alt = 'success';
        break;
      default:
        ({ title, subTitle, iconPath, alt } = getAdvancementErrorContent(errorCode, t));
        break;
    }
    return {
      title,
      subTitle,
      iconPath,
      alt,
    };
  };

  useEffect(() => {
    if (status === advancementStatusConstants.PROCESSED) refreshDataAdvancement();

    if (status === advancementStatusConstants.PENDING && advancementRequestCounter !== 0)
      checkStatusAdvancement(advancementStatus);
  }, [advancementRequestCounter]);

  const { title, subTitle, iconPath, alt } = getContent(status);
  return (
    <Layout>
      <StyledSpinner>
        {status === advancementStatusConstants.PENDING ? (
          <Spinner leyend="" size={breakpoint === 'xs' ? 'small' : 'medium'} />
        ) : (
          <img src={iconPath} alt={alt} />
        )}
      </StyledSpinner>
      <Title>{title}</Title>
      <Subtitle>{subTitle}</Subtitle>
    </Layout>
  );
};

export default AdvancementStatus;
